import BaseChart from './BaseChart';
import { CHART_ANIMATIONS } from '../config/animations';

/**
 * Scatter chart implementation using VChart
 */
class ScatterChart extends BaseChart {
  /**
   * Get chart specification for scatter chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();

    // Determine fields for x and y axes — auto-detect from data when not provided
    const xField = this.options.xField || this._resolveNameField();
    const yField = this.options.yField || this._resolveValueField();

    // Get optional fields for additional dimensions
    const seriesField = this.options.seriesField; // For grouping/coloring
    const sizeField = this.options.sizeField; // For point size
    const colorField = this.options.colorField || seriesField; // For point color

    // VChart scatter requires both axes to be numeric (linear).
    // When xField values are strings, map them to numeric indices
    // and use a label formatter to display the original category names.
    let data = this.prepareData();
    let categoryLabels = null;

    if (Array.isArray(this.data) && this.data.length > 0
        && typeof this.data[0][xField] === 'string') {
      const seen = new Set();
      const categories = [];
      for (const item of this.data) {
        const val = item[xField];
        if (!seen.has(val)) {
          seen.add(val);
          categories.push(val);
        }
      }
      const indexMap = {};
      categories.forEach((cat, i) => { indexMap[cat] = i; });
      categoryLabels = categories;

      if (data.length > 0 && data[0].values) {
        data = [{
          id: 'data',
          values: data[0].values.map(item => ({
            ...item,
            [xField]: indexMap[item[xField]]
          }))
        }];
      }
    }

    return {
      type: 'scatter',
      data,
      xField,
      yField,
      seriesField,
      sizeField,
      colorField,

      // Size scale configuration (if sizeField is provided)
      size: sizeField ? {
        type: 'linear',
        domain: this.getSizeDomain(),
        range: [4, 20],
        ...this.options.sizeScale
      } : undefined,

      // Color scale configuration (if colorField is provided)
      color: colorField ? {
        type: 'linear',
        ...this.options.colorScale
      } : undefined,

      // Point specific styling
      point: {
        style: {
          fillOpacity: 0.7,
          stroke: '#fff',
          lineWidth: 1,
          ...this.options.pointStyle
        },
        state: {
          hover: {
            fillOpacity: 1,
            stroke: '#fff',
            lineWidth: 2
          },
          ...this.options.pointState
        },
        shape: this.options.shape || 'circle',
        ...this.options.point
      },

      // Scatter label configuration
      label: {
        visible: this.options.showLabels === true,
        position: this.options.labelPosition || 'top',
        style: {
          fontSize: 10,
          fontWeight: 'normal',
          offset: [0, -10],
          ...this.options.labelStyle
        },
        formatter: this.options.labelFormatter
      },

      // Axes configuration
      axes: this.getAxesConfig(categoryLabels),

      // Crosshair for hover interactions
      crosshair: {
        xField: { visible: true },
        yField: { visible: true }
      },

      // Animation configuration
      animationAppear: {
        duration: 800,
        easing: 'cubicOut',
        ...this.options.animationAppear
      },

      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Get size domain for point size scaling
   * @returns {Array} Size domain [min, max]
   */
  getSizeDomain() {
    if (!this.options.sizeField || !this.data || this.data.length === 0) {
      return [0, 1];
    }
    
    const sizeField = this.options.sizeField;
    let min = Infinity;
    let max = -Infinity;
    
    this.data.forEach(item => {
      if (item[sizeField] < min) min = item[sizeField];
      if (item[sizeField] > max) max = item[sizeField];
    });
    
    return [min, max];
  }
  
  /**
   * Get axes configuration.
   * @param {string[]|null} categoryLabels When xField values were mapped from
   *   strings to numeric indices, this array maps index → original label.
   * @returns {Array} Axes configuration
   */
  getAxesConfig(categoryLabels = null) {
    // Build x-axis — scatter always requires type:'linear'.
    // Spread user xAxis overrides but strip type to prevent band axis.
    const { type: _ignoredType, ...restXAxis } = (this.options.xAxis || {});
    const xAxis = {
      orient: 'bottom',
      type: 'linear',
      label: {
        visible: true,
        style: {
          fontSize: 12,
          ...this.options.xAxisLabelStyle
        },
        formatter: this.options.xAxisLabelFormatter
      },
      title: {
        visible: !!this.options.xAxisTitle,
        text: this.options.xAxisTitle || '',
        style: {
          fontSize: 14,
          fontWeight: 'bold',
          ...this.options.xAxisTitleStyle
        }
      },
      ...restXAxis
    };

    if (categoryLabels) {
      xAxis.label.formatMethod = (value) => {
        const idx = Math.round(value);
        return (idx >= 0 && idx < categoryLabels.length)
          ? categoryLabels[idx]
          : '';
      };
      xAxis.min = -0.5;
      xAxis.max = categoryLabels.length - 0.5;
    }

    const yAxis = {
      orient: 'left',
      type: 'linear',
      label: {
        visible: true,
        style: {
          fontSize: 12,
          ...this.options.yAxisLabelStyle
        },
        formatter: this.options.yAxisLabelFormatter
      },
      title: {
        visible: !!this.options.yAxisTitle,
        text: this.options.yAxisTitle || '',
        style: {
          fontSize: 14,
          fontWeight: 'bold',
          ...this.options.yAxisTitleStyle
        }
      },
      grid: {
        visible: true,
        style: {
          stroke: '#E8E8E8',
          lineWidth: 1,
          lineDash: [4, 4],
          ...this.options.gridStyle
        }
      },
      ...this.options.yAxis
    };

    return [xAxis, yAxis];
  }
}

export default ScatterChart; 